import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { loginErrorMessage, useLogin } from './use-auth';
import { AuthShowcase } from './AuthShowcase';
import { BrandLogo } from '@/shared/brand/BrandLogo';

export function LoginPage(): JSX.Element {
  const login = useLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    login.mutate({ email, password });
  };

  return (
    <div
      className="cx"
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        background:
          'radial-gradient(900px 500px at 12% 0%, rgba(120,210,240,.20), transparent 60%), radial-gradient(800px 480px at 100% 100%, rgba(171,239,255,.18), transparent 60%), #EEF1F6',
      }}
    >
      <div
        className="card fade-in login-card"
        style={{ width: 1060, maxWidth: '100%', overflow: 'hidden', boxShadow: 'var(--sh-lg)', borderRadius: 24, minHeight: 660 }}
      >
        {/* LEFT — form */}
        <form onSubmit={submit} style={{ padding: '34px 48px 28px', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <div className="row between aic">
            <BrandLogo variant="wordmark-dark" height={40} />
            <div className="t-sm muted">
              ¿No tienes cuenta?{' '}
              <Link to="/register" style={{ color: 'var(--ink)', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                Regístrate
              </Link>
            </div>
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '20px 0' }}>
            <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.02em', margin: '0 0 6px' }}>Inicia sesión</h1>
            <p className="muted t-sm" style={{ margin: '0 0 22px' }}>
              Ingresa tus datos para acceder a Cajita.
            </p>

            <div className="col gap14">
              <div className="field">
                <label className="label">Correo electrónico</label>
                <div className="input-icon">
                  <Mail size={17} />
                  <input
                    className="input"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nombre@empresa.pe"
                  />
                </div>
              </div>
              <div className="field">
                <label className="label">Contraseña</label>
                <div className="input-icon">
                  <Lock size={17} />
                  <input
                    className="input"
                    type={show ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ paddingRight: 40 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShow((p) => !p)}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: 'var(--muted-2)' }}
                    aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {show ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>
              <div className="row between aic">
                <label className="row aic gap8 t-sm muted" style={{ cursor: 'pointer' }}>
                  <input type="checkbox" defaultChecked style={{ accentColor: '#0E7490', width: 15, height: 15 }} />
                  Recordarme
                </label>
                <a className="t-sm fw6" style={{ color: '#0E7490', textDecoration: 'none', cursor: 'pointer' }}>
                  ¿Olvidaste tu contraseña?
                </a>
              </div>
              {login.isError && (
                <div className="t-sm" style={{ color: 'var(--error)' }}>
                  {loginErrorMessage(login.error)}
                </div>
              )}
              <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={login.isPending} style={{ marginTop: 4 }}>
                {login.isPending ? 'Ingresando…' : 'Ingresar'}
                <ArrowRight size={18} />
              </button>
            </div>
          </div>

          <div className="t-xs muted2" style={{ textAlign: 'center' }}>
            © 2026 Cajita ERP · Todos los derechos reservados.
          </div>
        </form>

        {/* RIGHT — showcase */}
        <AuthShowcase />
      </div>
    </div>
  );
}
